import { Link } from "react-router-dom";
import {
  ArrowRight, Server, Shield, Zap, Clock, HeadphonesIcon, Globe, Lock,
  Cpu, Database, CheckCircle2, Star, Gauge, HardDrive,
} from "lucide-react";

const features = [
  { icon: Zap, title: "Yüksek Hız", description: "NVMe SSD depolama, LiteSpeed ve HTTP/3 ile dünya genelinde 100 ms altı yanıt süreleri.", accent: "col-span-1 md:col-span-2" },
  { icon: Clock, title: "Uptime Garantisi", description: "Yedekli altyapıda SLA ile güvence altına alınmış %99,99 çalışma süresi." },
  { icon: Shield, title: "Gelişmiş Güvenlik", description: "DDoS koruması, WAF ve zero-trust ağ tüm paketlerde standart." },
  { icon: HeadphonesIcon, title: "7/24 Destek", description: "Gerçek mühendisler 2 dakikadan kısa sürede yanıtlar — hangi saat diliminde olursanız olun.", accent: "col-span-1 md:col-span-2" },
];

const services = [
  { icon: Globe, title: "Web Hosting", description: "Linux, Windows, WordPress veya sınırsız hosting — ücretsiz SSL, otomatik yedek ve cPanel / Plesk ile.", bullets: ["NVMe SSD depolama", "Ücretsiz SSL", "Otomatik yedekleme"], to: "/hosting" },
  { icon: Server, title: "VPS / VDS", description: "KVM sanallaştırma ile tam root erişimli, dedicated kaynaklı ve yüksek performanslı sanal sunucular.", bullets: ["Tam root erişimi", "60 sn'de kurulum", "Snapshot yedek"], to: "/sunucular" },
  { icon: HardDrive, title: "Alan Adı", description: "500'den fazla uzantıda alan adı kaydı, transferi ve sorgulaması — WHOIS gizliliği hediye.", bullets: ["500+ uzantı", "Ücretsiz transfer", "WHOIS gizliliği"], to: "/alan-adi" },
  { icon: Lock, title: "Güvenlik & Firewall", description: "Yeni nesil WAF, DDoS koruması ve AI destekli zararlı yazılım taraması.", bullets: ["WAF & Anti-DDoS", "Malware tarama", "Brute-force koruması"], to: "/guvenlik" },
];

const testimonials = [
  { name: "Elif Yıldırım", role: "CTO · Pixelnest Studios", quote: "Altyapı maliyetlerimizi %40 düşürdük, sayfalarımız 80 ms altında yükleniyor. DR SOFT desteği bugüne kadar çalıştığımız en iyi ekip.", image: "https://images.unsplash.com/photo-1762522926157-bcc04bf0b10a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NzA0MDc0NXww&ixlib=rb-4.1.0&q=85" },
  { name: "Mert Kaya", role: "DevOps Lideri · Fintrail", quote: "Güvenlik duvarı, lansman gününde 40 Gbps'lik bir DDoS saldırısını sorunsuz durdurdu. Tek başına yıllarca servisi karşıladı.", image: "https://images.unsplash.com/photo-1769636929388-99eff95d3bf1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NzA0MDc0NXww&ixlib=rb-4.1.0&q=85" },
  { name: "Selin Aksoy", role: "Kurucu · Arclight AI", quote: "14 canlı servisi bir hafta sonunda taşıdık. Sıfır kesinti, faturada sıfır sürpriz.", image: "https://images.unsplash.com/photo-1576558656222-ba66febe3dec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NzA0MDc0NXww&ixlib=rb-4.1.0&q=85" },
];

const logos = ["Turkcell", "Getir", "Trendyol", "Hepsiburada", "Yemeksepeti", "Papara", "Insider", "Iyzico"];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-60 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-[700px] radial-glow-blue pointer-events-none" />
        <img src="https://images.unsplash.com/photo-1770885653473-ca48b4d69173?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzcwNjUwMDh8MA&ixlib=rb-4.1.0&q=85" alt="Teknoloji" className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0B10] via-[#0A0B10]/80 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0A0B10] to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-32 lg:pt-32 lg:pb-40">
          <div className="max-w-4xl animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md px-4 py-2 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
              </span>
              <span className="text-xs font-medium text-zinc-300">Yeni · Küresel Tier-1 ağ · 42 veri merkezi</span>
            </div>

            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
              Yüksek Performanslı Hosting
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-cyan-400 bg-clip-text text-transparent">
                & Güvenlik Çözümleri
              </span>
            </h1>

            <p className="mt-8 text-lg sm:text-xl text-zinc-400 max-w-2xl leading-relaxed">
              Web hosting, VPS / VDS sanal sunucular ve kurumsal güvenlik duvarı — stack'inizi
              hızlı, dayanıklı ve 7/24 korumalı tutmak için tasarlandı.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/fiyatlandirma" data-testid="hero-cta-plans" className="btn-shine inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-4 text-sm font-semibold text-white shadow-[0_0_40px_rgba(0,122,255,0.35)] transition-all">
                Paketleri İncele
                <ArrowRight size={16} />
              </Link>
              <Link to="/iletisim" data-testid="hero-cta-start" className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/30 bg-white/5 hover:bg-white/10 px-7 py-4 text-sm font-semibold text-white transition-all">
                Hemen Başla
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-zinc-500">
              {["30 gün iade garantisi", "Ücretsiz site taşıma", "Kartsız deneme"].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-blue-500" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { v: "%99,99", l: "Uptime garantisi" },
              { v: "< 80 ms", l: "Global TTFB" },
              { v: "42", l: "Veri merkezi" },
              { v: "120k+", l: "Aktif müşteri" },
            ].map((s, i) => (
              <div key={s.l} data-testid={`hero-stat-${i}`} className="glass rounded-2xl p-6 card-hover">
                <div className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{s.v}</div>
                <div className="mt-2 text-xs uppercase tracking-[0.18em] text-zinc-500">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOGOS */}
      <section className="border-y border-white/5 bg-[#07080C]/60 py-10 overflow-hidden">
        <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-6">
          Büyük ölçekte yayınlayan ekipler bize güveniyor
        </p>
        <div className="relative">
          <div className="flex gap-16 animate-marquee whitespace-nowrap">
            {[...logos, ...logos].map((logo, i) => (
              <span key={i} className="font-heading text-2xl font-semibold text-zinc-500/80 hover:text-white transition-colors">{logo}</span>
            ))}
          </div>
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#0A0B10] to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#0A0B10] to-transparent pointer-events-none" />
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative py-28 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">Neden DR SOFT</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">
              Ödün vermeyen ekipler için
              <br />
              <span className="text-zinc-500">tasarlanmış bir altyapı.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <div key={f.title} data-testid={`feature-card-${i}`} className={`card-hover glass rounded-3xl p-8 ${f.accent || ""}`}>
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-6">
                  <f.icon size={22} strokeWidth={1.6} className="text-blue-400" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">{f.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{f.description}</p>
              </div>
            ))}

            <div data-testid="feature-card-visual" className="card-hover glass rounded-3xl p-8 md:col-span-3 relative overflow-hidden min-h-[320px]">
              <div className="absolute right-0 top-0 h-full w-full md:w-2/3 opacity-60">
                <img src="https://images.pexels.com/photos/17489152/pexels-photo-17489152.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Sunucu odası" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#12141D] via-[#12141D]/60 to-transparent" />
              </div>
              <div className="relative max-w-md">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-6">
                  <Gauge size={22} strokeWidth={1.6} className="text-blue-400" />
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-semibold mb-3 tracking-tight">
                  Gerçekten hızlı bir kontrol paneli.
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6 max-w-sm">
                  VPS oluşturun, SSL sertifikalarınızı yenileyin, kaynakları ölçeklendirin veya
                  şüpheli bir node'u izole edin — tek bir hızlı panelden.
                </p>
                <Link to="/hosting" data-testid="feature-cta-explore" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors">
                  Platformu keşfet <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative py-28 bg-[#07080C]/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">Hizmetlerimiz</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">Ürün gibi tasarlanmış altyapı.</h2>
            </div>
            <Link to="/fiyatlandirma" data-testid="services-see-all" className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-300 hover:text-white transition-colors">
              Tüm fiyatları gör <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((s, i) => (
              <div key={s.title} data-testid={`service-card-${i}`} className="card-hover glass rounded-3xl p-8 flex flex-col">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-blue-700/10 border border-blue-500/20 flex items-center justify-center mb-6">
                  <s.icon size={24} strokeWidth={1.6} className="text-blue-400" />
                </div>
                <h3 className="font-heading text-2xl font-semibold mb-3">{s.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6">{s.description}</p>
                <ul className="space-y-2 mb-8 flex-1">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-3 text-sm text-zinc-300">
                      <CheckCircle2 size={15} className="text-blue-500 flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
                <Link to={s.to} data-testid={`service-learn-${i}`} className="inline-flex items-center gap-2 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors">
                  Daha fazla bilgi <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="relative py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">Performans</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight mb-6">
              Her istek ölçülür.
              <br />
              <span className="text-zinc-500">Her milisaniye değerlendirilir.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-8 max-w-lg">
              Dahili gözlemlenebilirlik ile gerçek zamanlı izleme, sentetik testler ve otomatik
              anomali tespiti — kullanıcılarınızdan önce problemleri yakalarsınız.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { i: Cpu, v: "12 CPU çekirdek", l: "her VPS pakette" },
                { i: Database, v: "NVMe Gen4", l: "her yerde" },
                { i: Shield, v: "WAF + DDoS", l: "ücretsiz dahil" },
                { i: Zap, v: "HTTP/3", l: "edge üzerinde" },
              ].map((item, idx) => (
                <div key={idx} className="glass rounded-2xl p-5">
                  <item.i size={18} strokeWidth={1.6} className="text-blue-400 mb-3" />
                  <div className="font-heading font-semibold">{item.v}</div>
                  <div className="text-xs text-zinc-500 mt-1">{item.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-10 bg-blue-600/10 blur-3xl rounded-full pointer-events-none" />
            <div className="relative rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
              <img src="https://images.unsplash.com/photo-1606778303077-3780ea8d5420?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxmaWJlciUyMG9wdGljJTIwY2FibGUlMjBibHVlfGVufDB8fHx8MTc3NzA2NTAwOHww&ixlib=rb-4.1.0&q=85" alt="Fiber optik" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B10] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                  <Zap size={16} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-400">Canlı edge gecikmesi</p>
                  <p className="font-heading text-xl font-semibold">73 ms · dünya ortalaması</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-28 bg-[#07080C]/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">Müşteri Yorumları</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">Rahat uyuyan ekipler.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <figure key={t.name} data-testid={`testimonial-${i}`} className="card-hover glass rounded-3xl p-8 flex flex-col">
                <div className="flex items-center gap-1 text-blue-400 mb-5">
                  {[...Array(5)].map((_, s) => (<Star key={s} size={14} fill="currentColor" strokeWidth={0} />))}
                </div>
                <blockquote className="text-zinc-200 leading-relaxed mb-6 flex-1">"{t.quote}"</blockquote>
                <figcaption className="flex items-center gap-3 pt-6 border-t border-white/5">
                  <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover border border-white/10" />
                  <div>
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-zinc-500">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="absolute inset-x-0 top-10 h-64 radial-glow-blue pointer-events-none" />
          <h2 className="relative font-heading text-4xl sm:text-6xl font-bold tracking-tight leading-[1.05]">
            Bir sonraki projenizi
            <br />
            <span className="text-blue-500">ölçeklenebilir altyapıda</span> yayınlayın.
          </h2>
          <p className="relative mt-6 text-zinc-400 max-w-xl mx-auto">
            Ücretsiz başlayın, ihtiyaç duydukça ölçeklendirin — sadece kullandığınız için ödeyin.
          </p>
          <div className="relative mt-10 flex items-center justify-center gap-4 flex-wrap">
            <Link to="/fiyatlandirma" data-testid="home-final-cta-plans" className="btn-shine inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-4 text-sm font-semibold text-white shadow-[0_0_40px_rgba(0,122,255,0.35)] transition-all">
              Paketleri gör <ArrowRight size={16} />
            </Link>
            <Link to="/iletisim" data-testid="home-final-cta-talk" className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/30 bg-white/5 hover:bg-white/10 px-7 py-4 text-sm font-semibold text-white transition-all">
              Satışla konuş
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
